import {
  CellInfo,
  RowInfo,
  ColumnProjection,
  GetColumnsProps,
} from './ticketListConfigurations.types';
import {
  voidedAccessor,
  ticketNumberAccessor,
  ticketedTimestampAccessor,
  truckAliasAccessor,
  driverNameAccessor,
  customerAccessor,
  orderNumberAccessor,
  addressAccessor,
  loadingPlantAccessor,
} from './ticketColumnAccessors';

export default function getColumns(props: GetColumnsProps): ColumnProjection[] {
  const { cellStyle, columnWidths, headerStyle } = props;

  const driverNameCell = (cellInfo: CellInfo) => cellInfo.value;

  const tsluAccessor = (row: RowInfo) =>
    voidedAccessor(row?.voided, row ? props.getSecondsSinceLastUpdate!(row, props.positions) : 0);

  const tsluCell = (cellInfo: CellInfo) =>
    cellInfo?.value ? props.renderTimeSinceLastUpdate!(Number(cellInfo.value)) : '';

  const projectAccessor = (row: RowInfo) =>
    voidedAccessor(row?.voided, row?.order?.projectName ?? '');

  const orderNumberCell = (cellInfo: CellInfo) =>
    cellInfo && cellInfo.original ? props.orderDetails!(cellInfo.original) : '';

  const widthConfigurationsMap: { [key: string]: number } = columnWidths.reduce(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue.id]: currentValue.value,
    }),
    {},
  );

  let tickets = [
    {
      Header: 'Ticket #',
      id: 'ticketNumber',
      accessor: ticketNumberAccessor,
      style: cellStyle,
      width: widthConfigurationsMap.ticketNumCurrent || 110,
      headerStyle,
    },
    {
      Header: 'Date',
      id: 'ticketedTimestamp',
      accessor: ticketedTimestampAccessor,
      style: cellStyle,
      width: widthConfigurationsMap.date || 120,
      headerStyle,
    },
    {
      Header: 'Truck',
      id: 'truckAlias',
      accessor: truckAliasAccessor,
      className: 'vehicle-cell',
      headerClassName: 'vehicle-header',
      width: widthConfigurationsMap.truckAliasCurrent || 145,
      style: cellStyle,
      headerStyle,
    },
    {
      Header: 'Current/Last Driver',
      id: 'driverName',
      accessor: driverNameAccessor,
      Cell: driverNameCell,
      className: 'driver-cell',
      headerClassName: 'driver-header',
      width: widthConfigurationsMap.driverFullName || 160,
      style: cellStyle,
      headerStyle,
    },
    {
      Header: 'Last Tablet Update',
      id: 'tslu',
      accessor: tsluAccessor,
      Cell: tsluCell,
      className: 'time-cell',
      headerClassName: 'time-header',
      style: cellStyle,
      width: widthConfigurationsMap.tslu || 195,
      headerStyle,
    },
    {
      Header: 'Order',
      id: 'orderNumber',
      accessor: orderNumberAccessor,
      Cell: orderNumberCell,
      className: 'order-cell',
      headerClassName: 'order-header',
      style: cellStyle,
      width: widthConfigurationsMap.order || 80,
      headerStyle,
      sortMethod: (
        a: ReturnType<typeof orderNumberAccessor>,
        b: ReturnType<typeof orderNumberAccessor>,
      ) => {
        let aValue: number = 0;
        let bValue: number = 0;
        if (typeof a === 'string') {
          aValue = parseInt(a, 10);
        } else if (typeof a === 'object' && a?.props?.children) {
          aValue = a.props.children;
        }

        if (typeof b === 'string') {
          bValue = parseInt(b, 10);
        } else if (typeof b === 'object' && b?.props?.children) {
          bValue = b.props.children;
        }

        aValue = Number.isNaN(aValue) ? 0 : aValue;
        bValue = Number.isNaN(bValue) ? 0 : bValue;
        return aValue - bValue;
      },
    },
    {
      Header: 'Customer',
      id: 'customerName',
      accessor: customerAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: widthConfigurationsMap.customerNameCurrent || 200,
      headerStyle,
    },
    {
      Header: 'Project',
      id: 'ProjectName',
      accessor: projectAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: widthConfigurationsMap.projectNameCurrent || 200,
      headerStyle,
    },
    {
      Header: 'Delivery Address',
      id: 'address',
      accessor: addressAccessor,
      className: 'address-cell',
      headerClassName: 'address-header',
      minWidth: 300,
      width: widthConfigurationsMap.addressCurrent || 300,
      style: cellStyle,
      headerStyle,
    },
    {
      Header: 'Loading Plant',
      id: 'loadingPlant',
      accessor: loadingPlantAccessor,
      className: 'ticket-cell',
      headerClassName: 'ticket-header',
      style: cellStyle,
      minWidth: 300,
      width: widthConfigurationsMap.ticketingPlantCurrent || 300,
      headerStyle,
    },
  ];

  // Check the Remove of Last Tablet Update if Is ScaleTrax
  if (props.isScaleTrax === true) tickets = tickets.filter(t => t.id !== 'tslu');
  return tickets;
}
